// R55 — CONTINUE WATCHING: THE FALLBACK WAS AN ARRAY, SO THE GUARD LET THE EMPTY SHELF THROUGH.
//
// THE HUNT is the pointer's, a fourth route on from R51/R52/R53/R54. `/api/videos/progress` has FOUR
// call sites in App.tsx (measured 2026-08-01) and they split three ways:
//
//   * **THE READ (two: My Room's `loadWatching` and the Library's `loadVideoProgress`).**
//     `r.ok ? r.json() : { videos: [] }` and then `if (Array.isArray(d.videos)) setWatching(d.videos)`.
//     **THE FABRICATED FALLBACK IS ITSELF AN ARRAY**, so it sails through the very guard that looks
//     like it is protecting her and **BLANKS Continue Watching** — R18 and R53/R54's finding again,
//     with the twist that the check written to catch a malformed payload is what admits this one.
//     In the Library the same read builds the resume-bar map, so every bar on every card vanished.
//   * **THE DELETE (`removeWatching`).** Paints first, then `.catch(() => {})`. R35 ruled this one
//     `NO_ASK_BY_DESIGN` (a Continue-watching row is cheap and the video is untouched), and that
//     ruling stands — **the optimistic paint is KEPT; what was missing is the PUT-BACK** (R54's
//     ruling word for word, one route over). The put-back is a RE-READ rather than a splice: the
//     server is what disagreed, and it is what orders this list.
//   * **THE WRITE (`saveProgress`).** `.then((r) => r.text()).catch(() => {})` — the answer was
//     drained and discarded. This one is the round's real judgement call and it is made here rather
//     than at the call site: **a progress ping is not a tap she is waiting on**, it fires on
//     `timeupdate` and on unmount, so a sentence per ping would be noise on every one of them.
//     What is NOT noise is the promise already printed under the player — *"Your place is saved as
//     you watch"* — going on saying that while every save is being refused. So the answer is read,
//     a success says NOTHING, and a refusal replaces that promise with the truth until a save lands.
//     One line, at player width, driven by what the server actually said.
//
// **THE THROTTLE IS DELIBERATELY LEFT ATTEMPT-BASED, AND THAT IS A RULING.** The obvious repair —
// stamp `lastSaved` only on success, so a refused ping is retried — was measured and rejected:
// `timeupdate` fires about four times a second, so during an outage the 10-second gate would be open
// on every event and the player would flood the route with a request every 250ms. Losing an
// intermediate ping costs nothing anyway, because the NEXT one records a LATER position. The only
// genuinely lossy case is a session where every save is refused, and that is exactly the case the
// sentence covers.
//
// The route can answer FIVE ways: **401** "Sign in to track your videos."; **HTTP 200 `{ ok: false }`**
// when `ensureUser` comes back empty — which until this round was `{ ok: true, videos: [] }`, i.e.
// **THE SERVER ITSELF CALLING A FAILED READ AN EMPTY SHELF**, and is now the same 200 + `{ ok: false }`
// five other routes in this house already answer with (R39/R47/R51/R53/R54); 200
// `{ ok: false, message: "No video named." }`; 200 `{ ok: false, message }` on a thrown write; and
// the plain `{ ok: true }`.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law), and the answer
// shape is deliberately `saved-verses.ts` / `saved-reports.ts`'s rather than a sixth key.

export const videoProgressPath = "/api/videos/progress";
export const videoProgressItemPath = (slug: string) => `${videoProgressPath}/${encodeURIComponent(slug)}`;

// The row as My Room draws it. `durationSeconds` is nullable because a video whose metadata never
// loaded has a position and no length, and the percentage law below has to survive that.
export type WatchedVideo = {
  slug: string;
  title: string;
  positionSeconds: number;
  durationSeconds: number | null;
  completed: boolean;
};

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — the session is gone (401). Nothing of hers is lost; she signs in and it saves.
//   refused — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and it sometimes said why.
//   offline — the request never landed. Nothing was decided; transport noise (R18).
export type ProgressAnswer =
  | { ok: true }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The two sentences the house owns, handed in by whoever holds the string catalog — this module
// imports nothing and cannot read it (`saved-books.ts`'s fallback-parameter shape).
export type ProgressCopy = { signIn: string; failed: string };

type Json = { ok?: boolean; message?: string };

// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer the route can
// give without a browser, a session or a database.
async function ask(path: string, init: RequestInit, doFetch: typeof fetch): Promise<ProgressAnswer> {
  try {
    const res = await doFetch(path, init);
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    // **`ok` IS READ, NOT THE STATUS CODE.** Every refusal this route composes except the 401
    // arrives at HTTP 200, which is precisely why draining the body to text saw none of them.
    if (res.ok && data?.ok !== false) return { ok: true };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// THE READ. **A FAILED READ IS NOT AN EMPTY SHELF** (R18): null, so a surface keeps whatever it was
// already showing rather than blanking Continue Watching because a request lost its way. The route's
// own empty list is a real `{ videos: [] }` — an account that has watched nothing — so the two are
// told apart by the SHAPE, not by the status code, and the fabricated fallback that used to wear
// that shape is gone.
export async function listWatching(doFetch: typeof fetch = fetch): Promise<WatchedVideo[] | null> {
  try {
    const res = await doFetch(videoProgressPath, { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { videos?: unknown } | null;
    return Array.isArray(data?.videos) ? (data.videos as WatchedVideo[]) : null;
  } catch {
    return null;
  }
}

// THE DELETE. The row leaves her screen first (R35's `NO_ASK_BY_DESIGN` ruling, kept), and this is
// the answer that decides whether it stays gone.
export function deleteWatching(slug: string, doFetch: typeof fetch = fetch): Promise<ProgressAnswer> {
  return ask(videoProgressItemPath(slug), { method: "DELETE" }, doFetch);
}

// THE WRITE. `keepalive` is EARNED here (the final save can fire as the tab is closing) and stays.
// The body is no longer drained to text and thrown away; it is READ.
export function saveVideoPosition(
  progress: { slug: string; positionSeconds: number; durationSeconds?: number },
  doFetch: typeof fetch = fetch,
): Promise<ProgressAnswer> {
  return ask(videoProgressPath, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(progress),
    keepalive: true,
  }, doFetch);
}

// The Library's resume bars read a MAP rather than the list, and building it is arithmetic a gate
// can run — including the two halves that used to be written inline: a missing position is 0 and a
// missing duration is null, never undefined, because the card's percentage law divides by it.
export function watchingMap(
  videos: WatchedVideo[],
): Record<string, { positionSeconds: number; durationSeconds: number | null }> {
  const map: Record<string, { positionSeconds: number; durationSeconds: number | null }> = {};
  for (const v of videos || []) {
    if (!v || typeof v.slug !== "string" || !v.slug) continue;
    map[v.slug] = {
      positionSeconds: Number(v.positionSeconds) || 0,
      durationSeconds: v.durationSeconds ?? null,
    };
  }
  return map;
}

// The sentence, derived from the answer rather than typed per surface. **A SUCCESS SAYS NOTHING** —
// the row leaving, or the place simply being kept, IS the answer, and a line under either would be
// noise (R51/R53/R54's ruling on the same question). Only a failure has something to tell her.
export function progressNote(answer: ProgressAnswer, copy: ProgressCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
